import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Hello Sofa: pack card (sofa cover + 2 cushion covers, colour and size chosen by the customer).
 * Adding to cart follows product-form.js: dispatch CartLinesUpdateEvent, POST /cart/add with the
 * cart sections, then resolve the event so the cart drawer re-renders and auto-opens.
 */
class HsPackButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-pack-button]');
    this.button?.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);

    if (!this.#selectedCoussin()) {
      const first = this.#coussinInputs()[0];
      if (first) first.checked = true;
    }
    this.#update();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
  }

  /** @returns {HTMLInputElement[]} */
  #coussinInputs() {
    return Array.from(this.querySelectorAll('.hs-pack__swatch-input'));
  }

  /** @returns {HTMLInputElement | undefined} */
  #selectedCoussin() {
    return this.#coussinInputs().find((input) => input.checked);
  }

  /** @returns {HTMLInputElement[]} */
  #sizeInputs() {
    return Array.from(this.querySelectorAll('.hs-pack__size input'));
  }

  /** @returns {{ variantId: string, quantity: number, regular: number, size: string } | null} */
  #selection() {
    const coussin = this.#selectedCoussin();
    const size = this.#sizeInputs().find((input) => input.checked)?.value;
    if (!coussin || !size) return null;
    const variantId = coussin.dataset[`v${size}`];
    if (!variantId) return null;
    return {
      variantId,
      quantity: Number(coussin.dataset[`q${size}`]) || 1,
      regular: Number(coussin.dataset[`p${size}`]) || 0,
      size,
    };
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    const amount = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return String(Math.round(cents / 100));
      if (key === 'amount_with_comma_separator') return amount.replace('.', ',');
      return amount;
    });
  }

  #handleChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.matches('.hs-pack__swatch-input, .hs-pack__size input')) return;
    this.#showError();
    this.#update();
  };

  /** Syncs sizes, image, name and prices with the selected colour and size. */
  #update() {
    const coussin = this.#selectedCoussin();
    if (!coussin) return;

    // Sizes this colour exists in; fall back to the other size when needed
    const sizes = this.#sizeInputs();
    for (const input of sizes) input.disabled = !coussin.dataset[`v${input.value}`];
    const checked = sizes.find((input) => input.checked);
    if (!checked || checked.disabled) {
      const available = sizes.find((input) => !input.disabled);
      if (available) available.checked = true;
    }
    const hint = /** @type {HTMLElement | null} */ (this.querySelector('[data-pack-hint]'));
    if (hint) hint.hidden = !sizes.some((input) => input.disabled);

    const selection = this.#selection();
    if (!selection) return;

    const percent = Number(this.dataset.percent) || 0;
    const packPrice = Math.round((selection.regular * (100 - percent)) / 100);

    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-pack-image]'));
    if (image) {
      if (coussin.dataset.image) {
        image.src = coussin.dataset.image;
        image.alt = `Housse de coussin ${coussin.dataset.name}`;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    const setText = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = text;
    };
    setText('[data-pack-name]', coussin.dataset.name || '');
    setText('[data-pack-variant]', `${coussin.dataset.name} · ${selection.size} × ${selection.size} cm`);
    setText('[data-pack-regular]', this.#money(selection.regular));
    setText('[data-pack-price]', this.#money(packPrice));

    const matching = /** @type {HTMLElement | null} */ (this.querySelector('[data-pack-matching]'));
    if (matching) matching.hidden = coussin.dataset.matching !== 'true';

    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-pack-link]'));
    if (link && coussin.dataset.url) link.href = coussin.dataset.url;

    if (this.button && this.dataset.context === 'cart' && !this.button.dataset.label) {
      this.button.textContent = `Ajouter les coussins · économisez ${this.#money(selection.regular - packPrice)}`;
    }
  }

  /** @returns {string | null} The variant currently selected in the product form of this section */
  #selectedSofaVariantId() {
    const section = this.closest('.shopify-section');
    const input = /** @type {HTMLInputElement | null} */ (
      section?.querySelector('product-form-component input[name="id"]')
    );
    return input?.value || null;
  }

  /** @param {string} [text] */
  #showError(text) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (!error) return;
    error.textContent = text || '';
    error.hidden = !text;
  }

  #handleClick = async () => {
    if (!this.button || this.button.getAttribute('aria-busy') === 'true') return;

    const selection = this.#selection();
    if (!selection) {
      this.#showError('Choisissez un coloris et une taille de coussin.');
      return;
    }

    const items = [];
    if (this.dataset.includeSofa === 'true') {
      const sofaVariantId = this.#selectedSofaVariantId();
      if (!sofaVariantId) {
        this.#showError('Choisissez d’abord la taille de votre housse.');
        return;
      }
      items.push({ id: Number(sofaVariantId), quantity: 1 });
    }
    items.push({ id: Number(selection.variantId), quantity: selection.quantity });

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.#showError();
    this.button.setAttribute('aria-busy', 'true');

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: this.dataset.context === 'cart' ? 'cart' : 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((result) => result.json());

      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((result) => result.json());

      if (response.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'hs-pack', itemCount },
        });
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        this.#showError(response.description || response.message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'hs-pack', itemCount, sections: response.sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#showError('Une erreur est survenue, merci de réessayer.');
    } finally {
      this.button?.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('hs-pack-button')) {
  customElements.define('hs-pack-button', HsPackButton);
}
